/**
 * Transition Rules: EnergyPlus 9.4 to 9.5
 *
 * Includes complex transformations that create new objects:
 * - Construction:InternalSource → Construction + ConstructionProperty:InternalHeatSource
 * - Coil:*:WaterToAirHeatPump:EquationFit → creates Curve objects from coefficients
 */

import type { VersionTransition, ObjectTransitionRule, MultiObjectResult } from '../types';
import type { IDFObject, IDFDocument, IDFField } from '../../parser/types';
import { createShiftedCopyMappings } from '../types';
import { copyField, createObject, addChange } from '../engine';

/**
 * Transform Construction:InternalSource to Construction + ConstructionProperty:InternalHeatSource
 *
 * Old format:
 * - A1: Name
 * - N1: Source Present After Layer Number
 * - N2: Temperature Calculation Requested After Layer Number
 * - N3: Dimensions for the CTF Calculation
 * - N4: Tube Spacing
 * - N5: Two-Dimensional Temperature Calculation Position
 * - A2-AN: Layer names (Outside to Inside)
 *
 * New format splits into:
 * - Construction with Name and Layer names
 * - ConstructionProperty:InternalHeatSource with heat source properties
 */
function transformConstructionInternalSource(object: IDFObject, _document: IDFDocument): MultiObjectResult {
  const result: MultiObjectResult = {
    primary: null,
    additional: [],
    changes: [],
    warnings: [],
  };

  const name = object.fields[0]?.value || '';
  const sourceAfterLayer = object.fields[1]?.value || '1';
  const tempCalcAfterLayer = object.fields[2]?.value || '1';
  const ctfDimensions = object.fields[3]?.value || '1';
  const tubeSpacing = object.fields[4]?.value || '0.0';
  const calcPosition = object.fields[5]?.value || '0.0';

  // Extract layer names (starting at field 6)
  const layerFields: IDFField[] = [];
  for (let i = 6; i < object.fields.length; i++) {
    const field = object.fields[i];
    if (field && field.value.trim()) {
      layerFields.push(copyField(field));
    }
  }

  // Create the standard Construction object
  const constructionObject = createObject(
    object.id,
    'Construction',
    [
      { value: name },
      ...layerFields,
    ],
    [...object.leadingComments],
    object.sourceLocation
  );

  // Create the ConstructionProperty:InternalHeatSource object
  const internalHeatSourceName = `${name}_InternalHeatSource`;
  const internalHeatSourceObject = createObject(
    `${object.id}_heatsource`,
    'ConstructionProperty:InternalHeatSource',
    [
      { value: internalHeatSourceName },
      { value: name }, // Construction Name
      { value: sourceAfterLayer }, // Thermal Source Present After Layer Number
      { value: tempCalcAfterLayer }, // Temperature Calculation Requested After Layer Number
      { value: ctfDimensions }, // Dimensions for the CTF Calculation
      { value: tubeSpacing }, // Tube Spacing
      { value: calcPosition }, // Two-Dimensional Temperature Calculation Position
    ],
    [],
    object.sourceLocation
  );

  result.primary = constructionObject;
  result.additional.push(internalHeatSourceObject);
  addChange(
    result.changes,
    'object_renamed',
    object.className,
    `Split Construction:InternalSource "${name}" into Construction and ConstructionProperty:InternalHeatSource`,
    name
  );

  return result;
}

/**
 * Transform Coil:Cooling:WaterToAirHeatPump:EquationFit to extract coefficients into Curve objects
 *
 * Creates Curve:QuadLinear and Curve:QuintLinear objects from inline coefficients
 */
function transformCoolingCoilWaterToAirHP(object: IDFObject, _document: IDFDocument): MultiObjectResult {
  const result: MultiObjectResult = {
    primary: null,
    additional: [],
    changes: [],
    warnings: [],
  };

  const name = object.fields[0]?.value || 'Coil';

  // Extract coefficients for Total Cooling Capacity Curve (fields 8-12 are coefficients 1-5)
  const totalCapCoeffs = [
    object.fields[8]?.value || '0',
    object.fields[9]?.value || '0',
    object.fields[10]?.value || '0',
    object.fields[11]?.value || '0',
    object.fields[12]?.value || '0',
  ];

  // Sensible Cooling Capacity Curve (fields 13-18 are coefficients 1-6)
  const sensCapCoeffs = [
    object.fields[13]?.value || '0',
    object.fields[14]?.value || '0',
    object.fields[15]?.value || '0',
    object.fields[16]?.value || '0',
    object.fields[17]?.value || '0',
    object.fields[18]?.value || '0',
  ];

  // Cooling Power Consumption Curve (fields 19-23 are coefficients 1-5)
  const powerCoeffs = [
    object.fields[19]?.value || '0',
    object.fields[20]?.value || '0',
    object.fields[21]?.value || '0',
    object.fields[22]?.value || '0',
    object.fields[23]?.value || '0',
  ];

  // Create Curve:QuadLinear for Total Cooling Capacity
  const totalCapCurveName = `${name}_TotCapCurve`;
  const totalCapCurve = createObject(
    `${object.id}_totcap_curve`,
    'Curve:QuadLinear',
    [
      { value: totalCapCurveName },
      { value: totalCapCoeffs[0] ?? '0' }, // Coefficient1
      { value: totalCapCoeffs[1] ?? '0' }, // Coefficient2
      { value: totalCapCoeffs[2] ?? '0' }, // Coefficient3
      { value: totalCapCoeffs[3] ?? '0' }, // Coefficient4
      { value: totalCapCoeffs[4] ?? '0' }, // Coefficient5
      { value: '-100' }, // Min x
      { value: '100' }, // Max x
      { value: '-100' }, // Min y
      { value: '100' }, // Max y
      { value: '-100' }, // Min z
      { value: '100' }, // Max z
      { value: '-100' }, // Min w
      { value: '100' }, // Max w
    ],
    [],
    object.sourceLocation
  );

  // Create Curve:QuintLinear for Sensible Cooling Capacity
  const sensCapCurveName = `${name}_SensCapCurve`;
  const sensCapCurve = createObject(
    `${object.id}_senscap_curve`,
    'Curve:QuintLinear',
    [
      { value: sensCapCurveName },
      { value: sensCapCoeffs[0] ?? '0' },
      { value: sensCapCoeffs[1] ?? '0' },
      { value: sensCapCoeffs[2] ?? '0' },
      { value: sensCapCoeffs[3] ?? '0' },
      { value: sensCapCoeffs[4] ?? '0' },
      { value: sensCapCoeffs[5] ?? '0' },
      { value: '-100' },
      { value: '100' },
      { value: '-100' },
      { value: '100' },
      { value: '-100' },
      { value: '100' },
      { value: '-100' },
      { value: '100' },
      { value: '-100' },
      { value: '100' },
    ],
    [],
    object.sourceLocation
  );

  // Create Curve:QuadLinear for Cooling Power
  const powerCurveName = `${name}_PowerCurve`;
  const powerCurve = createObject(
    `${object.id}_power_curve`,
    'Curve:QuadLinear',
    [
      { value: powerCurveName },
      { value: powerCoeffs[0] ?? '0' },
      { value: powerCoeffs[1] ?? '0' },
      { value: powerCoeffs[2] ?? '0' },
      { value: powerCoeffs[3] ?? '0' },
      { value: powerCoeffs[4] ?? '0' },
      { value: '-100' },
      { value: '100' },
      { value: '-100' },
      { value: '100' },
      { value: '-100' },
      { value: '100' },
      { value: '-100' },
      { value: '100' },
    ],
    [],
    object.sourceLocation
  );

  // Build new coil object with curve references replacing inline coefficients
  const newFields: IDFField[] = [
    copyField(object.fields[0]), // Name
    copyField(object.fields[1]), // Water Inlet Node
    copyField(object.fields[2]), // Water Outlet Node
    copyField(object.fields[3]), // Air Inlet Node
    copyField(object.fields[4]), // Air Outlet Node
    copyField(object.fields[5]), // Rated Air Flow Rate
    copyField(object.fields[6]), // Rated Water Flow Rate
    copyField(object.fields[7]), // Gross Rated Total Cooling Capacity
    { value: totalCapCurveName }, // Total Cooling Capacity Curve Name (replaces coefficients 8-12)
    { value: sensCapCurveName }, // Sensible Cooling Capacity Curve Name (replaces coefficients 13-18)
    { value: powerCurveName }, // Cooling Power Consumption Curve Name (replaces coefficients 19-23)
  ];

  // Copy remaining fields if any (starting from field 24)
  for (let i = 24; i < object.fields.length; i++) {
    newFields.push(copyField(object.fields[i]));
  }

  const newCoil = createObject(
    object.id,
    object.className,
    newFields,
    [...object.leadingComments],
    object.sourceLocation
  );

  result.primary = newCoil;
  result.additional.push(totalCapCurve, sensCapCurve, powerCurve);
  addChange(
    result.changes,
    'field_renamed',
    object.className,
    `Extracted inline coefficients from ${object.className} "${name}" into 3 Curve objects`,
    name
  );

  return result;
}

/**
 * Transform Coil:Heating:WaterToAirHeatPump:EquationFit to extract coefficients into Curve objects
 */
function transformHeatingCoilWaterToAirHP(object: IDFObject, _document: IDFDocument): MultiObjectResult {
  const result: MultiObjectResult = {
    primary: null,
    additional: [],
    changes: [],
    warnings: [],
  };

  const name = object.fields[0]?.value || 'Coil';

  // Heating Capacity Curve (fields 7-11 are coefficients 1-5)
  const capCoeffs = [
    object.fields[7]?.value || '0',
    object.fields[8]?.value || '0',
    object.fields[9]?.value || '0',
    object.fields[10]?.value || '0',
    object.fields[11]?.value || '0',
  ];

  // Heating Power Consumption Curve (fields 12-16 are coefficients 1-5)
  const powerCoeffs = [
    object.fields[12]?.value || '0',
    object.fields[13]?.value || '0',
    object.fields[14]?.value || '0',
    object.fields[15]?.value || '0',
    object.fields[16]?.value || '0',
  ];

  // Create Curve:QuadLinear for Heating Capacity
  const capCurveName = `${name}_CapCurve`;
  const capCurve = createObject(
    `${object.id}_cap_curve`,
    'Curve:QuadLinear',
    [
      { value: capCurveName },
      { value: capCoeffs[0] ?? '0' },
      { value: capCoeffs[1] ?? '0' },
      { value: capCoeffs[2] ?? '0' },
      { value: capCoeffs[3] ?? '0' },
      { value: capCoeffs[4] ?? '0' },
      { value: '-100' },
      { value: '100' },
      { value: '-100' },
      { value: '100' },
      { value: '-100' },
      { value: '100' },
      { value: '-100' },
      { value: '100' },
    ],
    [],
    object.sourceLocation
  );

  // Create Curve:QuadLinear for Heating Power
  const powerCurveName = `${name}_PowerCurve`;
  const powerCurve = createObject(
    `${object.id}_power_curve`,
    'Curve:QuadLinear',
    [
      { value: powerCurveName },
      { value: powerCoeffs[0] ?? '0' },
      { value: powerCoeffs[1] ?? '0' },
      { value: powerCoeffs[2] ?? '0' },
      { value: powerCoeffs[3] ?? '0' },
      { value: powerCoeffs[4] ?? '0' },
      { value: '-100' },
      { value: '100' },
      { value: '-100' },
      { value: '100' },
      { value: '-100' },
      { value: '100' },
      { value: '-100' },
      { value: '100' },
    ],
    [],
    object.sourceLocation
  );

  // Build new coil object with curve references replacing inline coefficients
  const newFields: IDFField[] = [
    copyField(object.fields[0]), // Name
    copyField(object.fields[1]), // Water Inlet Node
    copyField(object.fields[2]), // Water Outlet Node
    copyField(object.fields[3]), // Air Inlet Node
    copyField(object.fields[4]), // Air Outlet Node
    copyField(object.fields[5]), // Rated Air Flow Rate
    copyField(object.fields[6]), // Rated Water Flow Rate
    { value: capCurveName }, // Heating Capacity Curve Name (replaces coefficients 7-11)
    { value: powerCurveName }, // Heating Power Consumption Curve Name (replaces coefficients 12-16)
  ];

  // Copy remaining fields if any (starting from field 17)
  for (let i = 17; i < object.fields.length; i++) {
    newFields.push(copyField(object.fields[i]));
  }

  const newCoil = createObject(
    object.id,
    object.className,
    newFields,
    [...object.leadingComments],
    object.sourceLocation
  );

  result.primary = newCoil;
  result.additional.push(capCurve, powerCurve);
  addChange(
    result.changes,
    'field_renamed',
    object.className,
    `Extracted inline coefficients from ${object.className} "${name}" into 2 Curve objects`,
    name
  );

  return result;
}

export function createTransition_9_4_to_9_5(): VersionTransition {
  const objectRules = new Map<string, ObjectTransitionRule>();
  const variableMappings = new Map<string, string>();
  const deletedObjects = new Set<string>();
  const renamedObjects = new Map<string, string>();

  // Construction:AirBoundary - Remove fields 2-3
  objectRules.set('CONSTRUCTION:AIRBOUNDARY', {
    sourceClass: 'Construction:AirBoundary',
    targetClass: 'Construction:AirBoundary',
    fieldMappings: [
      { sourceIndex: 0, targetIndex: 0, type: 'copy' },
      { sourceIndex: 1, targetIndex: -1, type: 'delete' },
      { sourceIndex: 2, targetIndex: -1, type: 'delete' },
      ...createShiftedCopyMappings(3, 1, 20),
    ],
  });

  // ZoneAirMassFlowConservation - Enumeration conversion
  objectRules.set('ZONEAIRMASSFLOWCONSERVATION', {
    sourceClass: 'ZoneAirMassFlowConservation',
    targetClass: 'ZoneAirMassFlowConservation',
    fieldMappings: [
      {
        sourceIndex: 0,
        targetIndex: 0,
        type: 'transform',
        transform: (value) => {
          const upper = value.toUpperCase();
          if (upper === 'YES') return 'AdjustMixingOnly';
          if (upper === 'NO') return 'None';
          return value;
        },
      },
      ...createShiftedCopyMappings(1, 1, 10),
    ],
  });

  // Construction:InternalSource → Construction + ConstructionProperty:InternalHeatSource
  objectRules.set('CONSTRUCTION:INTERNALSOURCE', {
    sourceClass: 'Construction:InternalSource',
    targetClass: 'Construction',
    fieldMappings: [],
    multiObjectTransform: transformConstructionInternalSource,
  });

  // Coil:Cooling:WaterToAirHeatPump:EquationFit → creates Curve objects from coefficients
  objectRules.set('COIL:COOLING:WATERTOAIRHEATPUMP:EQUATIONFIT', {
    sourceClass: 'Coil:Cooling:WaterToAirHeatPump:EquationFit',
    targetClass: 'Coil:Cooling:WaterToAirHeatPump:EquationFit',
    fieldMappings: [],
    multiObjectTransform: transformCoolingCoilWaterToAirHP,
  });

  // Coil:Heating:WaterToAirHeatPump:EquationFit → creates Curve objects from coefficients
  objectRules.set('COIL:HEATING:WATERTOAIRHEATPUMP:EQUATIONFIT', {
    sourceClass: 'Coil:Heating:WaterToAirHeatPump:EquationFit',
    targetClass: 'Coil:Heating:WaterToAirHeatPump:EquationFit',
    fieldMappings: [],
    multiObjectTransform: transformHeatingCoilWaterToAirHP,
  });

  return {
    fromVersion: { major: 9, minor: 4 },
    toVersion: { major: 9, minor: 5 },
    objectRules,
    variableMappings,
    deletedObjects,
    renamedObjects,
  };
}
